import * as THREE from 'three';
import MathUtils from './MathUtils';
import SetObjectPositionAction from '../../actions/SetObjectPositionAction';
import { Alignment, defaultPivot, SaveModificationTypes } from '../../globals';
import Callout from "../Callout";
import DefaultSceneManager from "../DefaultSceneManager";
import Engine from '../Engine';
import ProjectSaver from '../ProjectSaver';
import { PivotSpace } from '../../interfaces/Pivot';

const tempScale = new THREE.Vector3();
const tempPosition = new THREE.Vector3();
const tempRotation = new THREE.Quaternion();
const tempMatrix = new THREE.Matrix4();
const tempVector = new THREE.Vector3();

export default class TransformUtils
{
	/**
     *
     *
     * @static
     * @param {*} object
     * @param {*} offset
     * @memberof TransformUtils
     */
	static applyPivot(object, offset, applyCalloutOffsetCorrection = false)
	{
		if(!object.userData.pivot) object.userData.pivot = JSON.parse(JSON.stringify(defaultPivot));

		if (DefaultSceneManager.getInstance().isDefaultObject(object.userData.guid))
		{
			DefaultSceneManager.getInstance().syncPivot(object, offset);
		}

		object.updateMatrixWorld();
		const initialWorldPosition = new THREE.Vector3();
		object.getWorldPosition(initialWorldPosition);

		const childrenWorldPositions = new Map();
		for (const child of object.children)
		{
			if (!(child instanceof Callout))
			{
				const worldPos = new THREE.Vector3();
				child.getWorldPosition(worldPos);
				childrenWorldPositions.set(child, worldPos);
			}
		}

		const relativeOffset = {};
		if (object.userData.pivot.space === PivotSpace.Relative)
		{
			relativeOffset.x = offset.x;
			relativeOffset.y = offset.y;
			relativeOffset.z = offset.z;
		}
		else
		{
			const offsetVector = new THREE.Vector3(offset.x, offset.y, offset.z);
			const worldQuaternion = new THREE.Quaternion();
			object.getWorldQuaternion(worldQuaternion);
			worldQuaternion.invert();
			offsetVector.applyQuaternion(worldQuaternion);
			relativeOffset.x = Math.abs(offsetVector.x) > 0.00001 ? offsetVector.x : 0;
			relativeOffset.y = Math.abs(offsetVector.y) > 0.00001 ? offsetVector.y : 0;
			relativeOffset.z = Math.abs(offsetVector.z) > 0.00001 ? offsetVector.z : 0;
		}

		const positionDelta = new THREE.Vector3(relativeOffset.x - object.userData.pivot.relativeOffset.x,
			relativeOffset.y - object.userData.pivot.relativeOffset.y,
			relativeOffset.z - object.userData.pivot.relativeOffset.z);

		if (!MathUtils.areVectorsEqual(positionDelta, { x: 0, y: 0, z: 0 }))
		{
			const quat = new THREE.Quaternion().copy(object.quaternion).invert();
			object.position.applyQuaternion(quat);
			object.position.add(positionDelta);
			quat.copy(object.quaternion);
			object.position.applyQuaternion(quat);

			object.updateMatrixWorld();
			object.matrixWorld.decompose(tempPosition, tempRotation, tempScale);
			tempRotation.invert();
			const transformedTranslation = new THREE.Vector3().subVectors(initialWorldPosition, tempPosition);
			transformedTranslation.applyQuaternion(tempRotation);
			transformedTranslation.divide(tempScale);

			const geometryClone = object.geometry.clone();
			geometryClone.translate(transformedTranslation.x, transformedTranslation.y, transformedTranslation.z);

			if (object.userData.pivot.hasGeometryClone) object.geometry.dispose();
			object.geometry = geometryClone;
			object.userData.pivot.hasGeometryClone = true;

			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Position);
			childrenWorldPositions.forEach(function(worldPosition, child)
			{
				TransformUtils.setWorldPosition(child, worldPosition);
				ProjectSaver.getInstance().markModifiedObject(child, SaveModificationTypes.Position);
			});

			if(applyCalloutOffsetCorrection && object.userData.annotation)
			{
				const callout = Callout.getCallout(object.userData.guid);
				if(callout)
				{
					const originOffset = callout.getOriginOffset();
					originOffset.x += transformedTranslation.x;
					originOffset.y += transformedTranslation.y;
					originOffset.z += transformedTranslation.z;
					callout.setOriginOffset(originOffset);
					ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Callout);
				}
			}

			object.userData.pivot.displayOffset.x = offset.x;
			object.userData.pivot.displayOffset.y = offset.y;
			object.userData.pivot.displayOffset.z = offset.z;
			object.userData.pivot.relativeOffset.x = relativeOffset.x;
			object.userData.pivot.relativeOffset.y = relativeOffset.y;
			object.userData.pivot.relativeOffset.z = relativeOffset.z;
			object.userData.pivot.geometryOffset.x += transformedTranslation.x;
			object.userData.pivot.geometryOffset.y += transformedTranslation.y;
			object.userData.pivot.geometryOffset.z += transformedTranslation.z;
		}
	}

	/**
     *
     *
     * @static
     * @param {*} object
     * @param {*} relativeOffset
     * @param {*} geometryOffset
     * @param {*} space
     * @memberof TransformUtils
     */
	static loadPivot(object, relativeOffset, geometryOffset, space)
	{
		if (!geometryOffset || Object.values(geometryOffset).some(value => value === undefined || value === null))
		{
			geometryOffset =
			{
				x: -relativeOffset.x / object.scale.x,
				y: -relativeOffset.y / object.scale.y,
				z: -relativeOffset.z / object.scale.z,
			};
		}

		const geometryClone = object.geometry.clone();
		geometryClone.translate(geometryOffset.x, geometryOffset.y, geometryOffset.z);
		object.geometry = geometryClone;
		object.userData.pivot = {};
		object.userData.pivot.hasGeometryClone = true;
		object.userData.pivot.space = space;
		object.userData.pivot.relativeOffset = {};
		object.userData.pivot.displayOffset = {};
		object.userData.pivot.geometryOffset = {};
		object.userData.pivot.relativeOffset.x = relativeOffset.x;
		object.userData.pivot.relativeOffset.y = relativeOffset.y;
		object.userData.pivot.relativeOffset.z = relativeOffset.z;
		object.userData.pivot.geometryOffset.x = geometryOffset.x;
		object.userData.pivot.geometryOffset.y = geometryOffset.y;
		object.userData.pivot.geometryOffset.z = geometryOffset.z;

		if (space === PivotSpace.Relative)
		{
			object.userData.pivot.displayOffset.x = relativeOffset.x;
			object.userData.pivot.displayOffset.y = relativeOffset.y;
			object.userData.pivot.displayOffset.z = relativeOffset.z;
		}
		else
		{
			const convertedOffset = TransformUtils.getConvertedPivotOffsetSpace(object, relativeOffset, PivotSpace.Absolute);
			object.userData.pivot.displayOffset.x = convertedOffset.x;
			object.userData.pivot.displayOffset.y = convertedOffset.y;
			object.userData.pivot.displayOffset.z = convertedOffset.z;
		}
	}

	/**
     *
     *
     * @static
     * @param {*} object
     * @param {*} offset
     * @param {*} space
     * @returns
     * @memberof TransformUtils
     */
	static getConvertedPivotOffsetSpace(object, offset, space)
	{
		const offsetVector = new THREE.Vector3(offset.x, offset.y, offset.z);
		const worldQuaternion = new THREE.Quaternion();
		object.getWorldQuaternion(worldQuaternion);
		if (space === PivotSpace.Relative)
		{
			//from absolute to relative
			worldQuaternion.invert();
		}

		offsetVector.applyQuaternion(worldQuaternion);
		offsetVector.x = Math.abs(offsetVector.x) > 0.00001 ? offsetVector.x : 0;
		offsetVector.y = Math.abs(offsetVector.y) > 0.00001 ? offsetVector.y : 0;
		offsetVector.z = Math.abs(offsetVector.z) > 0.00001 ? offsetVector.z : 0;

		return offsetVector;
	}

	/**
     *
     *
     * @static
     * @param {*} object
     * @param {*} position
     * @memberof TransformUtils
     */
	static setWorldPosition(object, position)
	{
		if (!object.parent || (object.parent.parent && object.parent.parent.type === 'Scene'))
		{
			object.position.copy(position);
		}
		else
		{
			tempPosition.copy(position);
			object.parent.worldToLocal(tempPosition);
			object.position.copy(tempPosition);
		}
	}

	/**
     *
     *
     * @static
     * @param {*} object
     * @param {*} quat
     * @memberof TransformUtils
     */
	 static setWorldQuaternion(object, quat)
	 {
		 if (!object.parent || (object.parent.parent && object.parent.parent.type === 'Scene'))
		 {
			 object.quaternion.copy(quat);
		 }
		 else
		 {
			 object.parent.getWorldQuaternion(tempRotation);
			 tempRotation.invert();
			 tempRotation.multiply(quat);
			 object.quaternion.copy(tempRotation);
		 }
	 }
 
	 /**
	  *
	  *
	  * @static
	  * @param {*} object
	  * @param {*} translation
	  * @memberof TransformUtils
	  */
	 static translateWorldSpace(object, translation)
	 {
		 if (!object.parent || (object.parent.parent && object.parent.parent.type === 'Scene'))
		 {
			 object.position.add(translation);
		 }
		 else
		 {
			object.parent.localToWorld(object.position);
			object.position.add(translation);
			object.parent.worldToLocal(object.position);
		 }
	 }
 
	 /**
	  *
	  *
	  * @static
	  * @param {*} object
	  * @param {*} pivot
	  * @param {*} axis
	  * @param {*} angle
	  * @memberof TransformUtils
	  */
	  static rotateAround(object, pivot, axis, angle)
	  {
		 const objectWorldPostion = new THREE.Vector3();
		 object.getWorldPosition(objectWorldPostion);
 
		 const towardsPivot = new THREE.Vector3().subVectors(pivot, objectWorldPostion);
		 this.translateWorldSpace(object, towardsPivot);
 
		 const objectWorldQuaternion = new THREE.Quaternion();    
		object.getWorldQuaternion(objectWorldQuaternion);  
 
		 const localAxis = axis.clone().applyQuaternion(objectWorldQuaternion.invert()).normalize();
		 object.rotateOnAxis(localAxis, angle);
		 towardsPivot.applyAxisAngle(axis.clone(), angle);
		 this.translateWorldSpace(object, towardsPivot.multiplyScalar(-1));
	  }

	/**
	 * aligns the selected objects along one aspect of the last selected object's (the reference object) bounding box
	 * it will set the selected objects' positions so that their bounding boxes will match the reference object's, according to the given alignment
	 * @static
	 * @param {Alignment} alignment defines which aspect of the reference object will be used for object alignment
	 * @memberof TransformUtils
	 */
	 static alignBounds(alignment)
	 {
		 const selectedObjects = Engine.getInstance().getPickingManager().getCurrentSelection().objects;
		 if (selectedObjects.length < 2) return;
		 const activeObject = selectedObjects[selectedObjects.length - 1];
		 const activeBounds = new THREE.Box3().setFromObject(activeObject);
 
		 const objects = [];
		 const oldPositions = [];
		 const newPositions = [];
 
		 const currentBounds = new THREE.Box3();
		 let currentObject = null;
 
		 switch (alignment)
		 {
			 case Alignment.TOP: // max Y
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
					 const diff = activeBounds.max.y - currentBounds.max.y;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(0, 1, 0).multiplyScalar(diff)));
				 }
				 break;
			 case Alignment.BOTTOM: // min Y
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
					 const diff = activeBounds.min.y - currentBounds.min.y;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(0, 1, 0).multiplyScalar(diff)));
				 }
				 break;
			 case Alignment.LEFT: //max X
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
					 const diff = activeBounds.min.x - currentBounds.min.x;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(1, 0, 0).multiplyScalar(diff)));
				 }
				 break;
			 case Alignment.RIGHT: // min X
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
					 const diff = activeBounds.max.x - currentBounds.max.x;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(1, 0, 0).multiplyScalar(diff)));
				 }
				 break;
			 case Alignment.FRONT: // max Z
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
					 const diff = activeBounds.max.z - currentBounds.max.z;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(0, 0, 1).multiplyScalar(diff)));
				 }
				 break;
			 case Alignment.BACK: // min Z
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
					 const diff = activeBounds.min.z - currentBounds.min.z;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(0, 0, 1).multiplyScalar(diff)));
				 }
				 break;
			 case Alignment.CENTERX:
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
					 activeBounds.getCenter(tempVector);
					 const activeBoundsX = tempVector.x;
					 currentBounds.getCenter(tempVector);
					 const diff = activeBoundsX - tempVector.x;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(1, 0, 0).multiplyScalar(diff)));
				 }
				 break;
			 case Alignment.CENTERY:
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
 
					 activeBounds.getCenter(tempVector);
					 const activeBoundsY = tempVector.y;
					 currentBounds.getCenter(tempVector);
					 const diff = activeBoundsY - tempVector.y;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(0, 1, 0).multiplyScalar(diff)));
				 }
				 break;
			 case Alignment.CENTERZ:
				 for (let i = 0; i < selectedObjects.length - 1; i++)
				 {
					 currentObject = selectedObjects[i];
					 currentBounds.setFromObject(currentObject);
 
					 activeBounds.getCenter(tempVector);
					 const activeBoundsZ = tempVector.z;
					 currentBounds.getCenter(tempVector);
					 const diff = activeBoundsZ - tempVector.z;
 
					 objects.push(currentObject);
					 oldPositions.push(new THREE.Vector3().copy(currentObject.position));
					 newPositions.push(new THREE.Vector3().copy(currentObject.position).add(new THREE.Vector3(0, 0, 1).multiplyScalar(diff)));
				 }
				 break;
			 default:
				 console.error("unknown alignment type : " + alignment);
				 break;
		 }
 
		 new SetObjectPositionAction(objects, newPositions, oldPositions);
	 }

	/**
	 * aligns the selected objects along the given axis, according to the last object selected (the reference object)
	 * this works by simply setting each selected object's position value to the same value of the reference object's, according to the given axis
	 * @static
	 * @param {THREE.Vector3} axis defines which position values should be copied from the reference object. while it can technically be any valid THREE.Vector3 value, this function is meant to work with an axis vector (e.g. up, left, etc)
	 * @memberof TransformUtils
	 */
	static alignAxis(axis)
	{
		const selectedObjects = Engine.getInstance().getPickingManager().getCurrentSelection().objects;
		if (selectedObjects.length < 2) return;
		const activeObject = selectedObjects[selectedObjects.length - 1];

		const objects = [];
		const oldPositions = [];
		const newPositions = [];

		const tempWorldPosition = new THREE.Vector3();
		for (let i = 0; i < selectedObjects.length - 1; i++)
		{
			const current = selectedObjects[i];
			objects[i] = current;
			current.getWorldPosition(tempWorldPosition);
			oldPositions[i] = new THREE.Vector3().copy(tempWorldPosition);

			activeObject.getWorldPosition(tempWorldPosition);
			newPositions[i] = new THREE.Vector3(axis.x ? tempWorldPosition.x : current.position.x,
				axis.y ? tempWorldPosition.y : current.position.y,
				axis.z ? tempWorldPosition.z : current.position.z);
		}

		new SetObjectPositionAction(objects, newPositions, oldPositions);
	}
}